import { Component } from '@angular/core';
import { ViewQuizResultDialogComponent } from '../../quiz/view-quiz-result-dialog/view-quiz-result-dialog.component';
import { QuizDialogComponent } from '../../quiz/quiz-dialog/quiz-dialog.component';
import { QuizStatus } from '@edtech-ui/constants/quiz-status.enum';
import { Quiz, QuizResponse } from '@edtech-ui/core/models/shared-resources/shared-resource.model';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { ToastrService } from 'ngx-toastr';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { finalize, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-student-quiz',
	standalone: true,
	imports: [RouterModule, MatIconModule, MatButtonModule, CommonModule,MatProgressSpinnerModule],
	templateUrl: './student-quiz.component.html',
	styleUrl: './student-quiz.component.scss'
})
export class StudentQuizComponent {
	classroomId: number | null = null;
	quizResources: Quiz[] = [];
	private routerSub: Subscription | null = null;
	private lastFetchedClassroomId: number | null = null;
	private studentId: string | null = null;
	isLoading = false;
	QuizStatus = QuizStatus;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private dialog: MatDialog,
		private _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private _authService: AuthServiceService
	) { }

	ngOnInit() {
		this.studentId = this._authService.getUserId();

		if (!this.studentId) {
			this._toastr.error('Student ID not found. Please log in again.');
			this.router.navigate(['/login']);
			return;
		}

		this.route.parent?.paramMap.subscribe(params => {
			const id = params.get('id');
			if (id) {
				this.classroomId = +id;
				if (this.classroomId !== this.lastFetchedClassroomId) {
					this.fetchQuizzes();
					this.lastFetchedClassroomId = this.classroomId;
				}
			} else {
				this._toastr.error('Classroom ID not found in the URL.');
				this.router.navigate(['/student-dashboard/classrooms']);
			}
		});
	}

	fetchQuizzes() {
		if (!this.classroomId || !this.studentId) {
			this._toastr.error('Classroom ID or Student ID is missing.');
			this.router.navigate(['/student-dashboard/classrooms']);
			return;
		}
		this.isLoading = true;
		this._quizService.getStudentQuizzes(this.classroomId, this.studentId).pipe(
			finalize(()=>{
				this.isLoading = false;
			})).subscribe({
			next: (response: QuizResponse) => {
				if (response.Success && response.Data) {
					const completedQuizzes = response.Data.completed.map(quiz => ({
						title: quiz.quiz_name,
						date: this.formatDate(quiz.finished_at || quiz.published_date),
						duration: 'N/A',
						questions: quiz.total_questions || 0,
						score: quiz.score,
						quizId: quiz.quiz_id,
						attemptId: quiz.attempt_id,
						status: quiz.status as QuizStatus
					}));

					const pendingQuizzes = response.Data.pending.map(quiz => ({
						title: quiz.quiz_name,
						date: this.formatDate(quiz.published_date),
						duration: 'N/A',
						questions: quiz.total_questions,
						score: undefined,
						quizId: quiz.quiz_id,
						attemptId: undefined,
						status: quiz.status as QuizStatus
					}));

					const inProgressQuizzes = response.Data.inprogress.map(quiz => ({
						title: quiz.quiz_name,
						date: this.formatDate(quiz.start_time || quiz.published_date),
						duration: this.calculateDuration(quiz.start_time, quiz.end_time),
						questions: quiz.total_questions ?? 0,
						score: quiz.score ?? 'N/A',
						quizId: quiz.quiz_id,
						attemptId: quiz.attempt_id,
						status: quiz.status as QuizStatus
					}));

					this.quizResources = [...pendingQuizzes, ...completedQuizzes, ...inProgressQuizzes];
				} else {
					this._toastr.error(response.Message || 'Failed to fetch quizzes.');
					this.quizResources = [];
				}
			},
			error: (err) => {
				if (err.status === 404) {
					this._toastr.error('Quiz endpoint not found. Please check the server configuration.');
				} else {
					this._toastr.error('Failed to fetch quizzes due to a server error.');
				}
				this.quizResources = [];
				this.isLoading = false;
			}
		});
	}

	formatDate(dateStr: string): string {
		const date = new Date(dateStr);
		const day = date.getDate().toString().padStart(2, '0');
		const month = date.toLocaleString('default', { month: 'short' });
		const year = date.getFullYear();
		return `${day}-${month}-${year}`;
	}

	openTakeQuizPopup(quiz: Quiz) {
		this.dialog.open(QuizDialogComponent, {
			width: '100%',
			maxWidth: '440px',
			panelClass: 'custom-dialog-panel',
			disableClose: true,
			data: { quizId: quiz.quizId, status: quiz.status }
		}).afterClosed().subscribe(result => {
			if (result?.submitted) this.fetchQuizzes();
		});
	}

	viewQuizResult(quiz: Quiz) {
		if (!quiz.attemptId) {
			this._toastr.error('Attempt ID is missing for this quiz.');
			return;
		}

		this.dialog.open(ViewQuizResultDialogComponent, {
			width: 'auto',
			maxWidth: 'none',
			panelClass: 'custom-dialog-panel',
			disableClose: true,
			data: {
				quizId: quiz.quizId,
				attemptId: quiz.attemptId,
				status: quiz.status,
				score: quiz.score
			}
		});
	}

	calculateDuration(start: string, end: string): string {
		if (!start || !end) return 'N/A';
		const diffMs = new Date(end).getTime() - new Date(start).getTime();
		const minutes = Math.floor(diffMs / 60000);
		return `${minutes} min`;
	}

	goToQuizDetailPage(quiz_id: string, attempt_id?: string) {
		this.router.navigate(['student-dashboard/quiz', quiz_id], { queryParams: { attemptId: attempt_id } });
	}

	ngOnDestroy() {
		if (this.routerSub) {
			this.routerSub.unsubscribe();
		}
	}

}